import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User } from './../models/user';
import { Species } from '../models/species';
import { Disease } from '../models/disease';
import { SurgicalOperation } from '../models/surgicalOperation';
import { FlowControl } from '../models/flowControl';
import { OrganizationFosterFamily, AnimalRefugeCategory, Organization } from '../models/organization';
import { AnimalTemporaryAdoption, AnimalShare, AnimalShareUser, AnimalShareAnimal, Animal, AnimalVaccineValence, AnimalMapPlanItem, AnimalVaccine, AnimalDisease, AnimalSurgicalOperation, AnimalWeight, AnimalDocument, AnimalTag } from './../models/animal';
import { VaccineValence, VaccineLabel, VaccineConfiguration } from './../models/vaccine';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { DiseaseService } from './disease.service';
import { SurgicalOperationService } from './surgical-operation.service';
import { UserService } from './user.service';
import { DateUtilsService } from './date-utils.service';
import { IndexedDbService } from './indexed-db.service';
import { OrganizationService } from './organization.service';
import { blobToBase64 } from '../utils'
import { TranslateService } from '@ngx-translate/core';
import { AgendaEvent, AgendaEventStatut, AgendaEventType } from '../models/agenda';
import { AgendaService } from './agenda.service';

@Injectable({
  providedIn: 'root'
})
export class AnimalService {
  animals: Animal[] = []; //Liste des animaux en cache
  speciess: Species[] = [];
  animalShares: AnimalShare[] = [];

  constructor(
      private http: HttpClient,
      public storageService: StorageService,
      public networkService: NetworkService,
      private translate: TranslateService,
      private dateUtils: DateUtilsService,
      private userService: UserService,
      private diseaseService: DiseaseService,
      private surgicalOperationService: SurgicalOperationService,
      public indexedDbService: IndexedDbService,
      public organizationService: OrganizationService,
      public alertController: AlertController) { }

  dbSaveAnimal(agendaService: AgendaService, animal: Animal) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animal.id;
        formData["userId"] = animal.userId;
        formData["nameOfficial"] = animal.nameOfficial;
        formData["name"] = animal.name;
        formData["affix"] = animal.affix;
        formData["chip"] = animal.chip;
        formData["race"] = animal.race;
        formData["birthday"] = animal.birthday;
        formData["isHidden"] = animal.isHidden;
        formData["passportCode"] = animal.passportCode;
        formData["isLOF"] = animal.isLOF;
        formData["isConfirmed"] = animal.isConfirmed;
        formData["gender"] = animal.gender;
        formData["isDead"] = animal.isDead;
        formData["dateDeath"] = animal.dateDeath;
        formData["mapPlanItemId"] = animal.mapPlanItem?.id;
        formData["description"] = animal.description;
        formData["geneticTests"] = animal.geneticTests;
        formData["category"] = animal.category;
        formData["speciesId"] = animal.species?.id;
        formData["fosterFamilyId"] = animal.fosterFamily?.id;
        formData["displayOnPublicPage"] = animal.displayOnPublicPage;

        formData["specificFood"] = animal.specificFood;
        formData["descriptionFood"] = animal.descriptionFood;

        formData["refugeHistory"] = animal.refugeHistory;
        formData["refugeCharacter"] = animal.refugeCharacter;
        formData["refugeAgreements"] = animal.refugeAgreements;
        formData["refugeAdoptingProfile"] = animal.refugeAdoptingProfile;
        formData["refugeAdoptionAvailable"] = animal.refugeAdoptionAvailable;
        formData["refugeVisibleOnPublicPage"] = animal.refugeVisibleOnPublicPage;
        formData["refugeBitingIs"] = animal.refugeBitingIs;
        formData["refugeMuzzleIs"] = animal.refugeMuzzleIs;
        formData["refugeCategoryId"] = animal.refugeCategory?.id ?? null;
        
        formData["refugeCollarIs"] = animal.refugeCollarIs;
        formData["refugeHarnessIs"] = animal.refugeHarnessIs;
        formData["refugeDogOkLevel"] = animal.refugeDogOkLevel;
        formData["refugeCatOkLevel"] = animal.refugeCatOkLevel;
        formData["refugeWomanOkLevel"] = animal.refugeWomanOkLevel;
        formData["refugeManOkLevel"] = animal.refugeManOkLevel;
        formData["refugeKidsOkLevel"] = animal.refugeKidsOkLevel;
        formData["refugeCarOkLevel"] = animal.refugeCarOkLevel;
        formData["refugeBikeOkLevel"] = animal.refugeBikeOkLevel;
        formData["refugeRunnerOkLevel"] = animal.refugeRunnerOkLevel;
        formData["refugeHealth"] = animal.refugeHealth;
        formData["refugeEnclosureOKIs"] = animal.refugeEnclosureOKIs;
        formData["refugeWalkingInfo"] = animal.refugeWalkingInfo;
        formData["organizationDateEntry"] = animal.organizationDateEntry;

        //Pedigree
        formData["pedigreeUserId"] = animal.pedigreeUserId;
        formData["pedigreeAnimalIs"] = animal.pedigreeAnimalIs;
        formData["pedigreeOwnerName"] = animal.pedigreeOwnerName;
        formData["fatherId"] = animal.father?.id;
        formData["motherId"] = animal.mother?.id;

        //Eleveur
        formData["breederReserveIs"] = animal.breederReserveIs;


        if(animal.avatarNewFileName != null) {
          formData["avatar"] = {'base64': animal.avatarBase64, 'name': animal.avatarNewFileName}
        }

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimal/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (animal.id == null) {
              animal.id = response['id']
            }

            if(animal.userId == this.userService.user?.id) {
              this.dbGetAnimal(agendaService, true, animal.id).then((data: Animal | null | undefined) => {
                resolve(animal);
              });
            } else {
              resolve(animal);
            }
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animal, 'action': 'save'}).subscribe(() => {});
        resolve(animal)
      }
    });
  }

  dbSaveAnimalWeight(animal: Animal, weight: AnimalWeight): Promise<AnimalWeight> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = weight.id;
        formData["animalId"] = animal.id;
        formData["date"] = weight.date;
        formData["weight"] = weight.weight;

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/SaveAnimalWeight/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            weight.id = response['id']
            animal.weights = animal.weights.sort((a: AnimalWeight, b: AnimalWeight) => (b.date + '_' + b.weight).localeCompare(a.date + '_' + a.weight))
            resolve(weight);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': weight, 'action': 'save'}).subscribe(() => {});
        resolve(weight)
      }
    });
  }

  dbSaveAnimalDisease(animal: Animal, disease: AnimalDisease) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = disease.id;
        formData["animalId"] = animal.id;
        formData["date"] = disease.date;
        formData["dateEnd"] = disease.dateEnd;
        formData["isEnd"] = disease.isEnd;
        formData["diseaseId"] = disease.disease?.id;

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/SaveAnimalDisease/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            disease.id = response['id']
            resolve(disease);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': disease, 'action': 'save'}).subscribe(() => {});
        resolve(disease)
      }
    });
  }

  dbSaveAnimalSurgicalOperation(animal: Animal, surgicalOperation: AnimalSurgicalOperation) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = surgicalOperation.id;
        formData["animalId"] = animal.id;
        formData["date"] = surgicalOperation.date;
        formData["surgicalOperationId"] = surgicalOperation.surgicalOperation?.id;

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/SaveAnimalSurgicalOperation/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            surgicalOperation.id = response['id']
            resolve(surgicalOperation);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': surgicalOperation, 'action': 'save'}).subscribe(() => {});
        resolve(surgicalOperation)
      }
    });
  }

  dbSaveAnimalDocument(animal: Animal, document: AnimalDocument) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = document.id;
        formData["animalId"] = animal.id;
        formData["file"] = document.id;
        formData["base64"] = document.id == null ? document.base64: null,
        formData["name"] = document.name;
        formData["fileName"] = document.fileName;

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/SaveAnimalDocument/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            document.id = response['id']
            resolve(document);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': document, 'action': 'save'}).subscribe(() => {});
        resolve(document)
      }
    });
  }

  dbSaveAnimalTemporaryAdoption(agendaService: AgendaService, animal: Animal, temporaryAdoption: AnimalTemporaryAdoption, markRefugeAdoptionAvailable: boolean | null) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = temporaryAdoption.id;
        formData["animalId"] = animal.id;
        formData["dateStart"] = temporaryAdoption.dateStart;
        formData["dateEnd"] = temporaryAdoption.dateEnd;
        formData["firstName"] = temporaryAdoption.firstName;
        formData["lastName"] = temporaryAdoption.lastName;
        formData["email"] = temporaryAdoption.email;
        formData["addr"] = temporaryAdoption.addr;
        formData["phoneNumber"] = temporaryAdoption.phoneNumber;
        formData["markRefugeAdoptionAvailable"] = markRefugeAdoptionAvailable;

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimalTemporaryAdoption/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            temporaryAdoption.id = response['id']

            if(markRefugeAdoptionAvailable != null) {
              animal.refugeAdoptionAvailable = markRefugeAdoptionAvailable;
            }

            this.dbGetAnimal(agendaService, true, animal.id).then((data: Animal | null | undefined) => {
              resolve(temporaryAdoption);
            });
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': temporaryAdoption, 'action': 'save'}).subscribe(() => {});
        resolve(temporaryAdoption)
      }
    });
  }
  
  dbGetAnimal(agendaService: AgendaService, forceUpdate: boolean, animalId: number | null): Promise<Animal | null | undefined> {
    var cache: Animal | undefined = this.animals.find(a => a.id == animalId)
    return new Promise((resolve) => {
      if ((!animalId || forceUpdate || !cache) && this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });

        var url = environment.API_URL + 'animal/ws_private/' + animalId + '/GetAnimal/';
        if(animalId == null)
          url = environment.API_URL + 'animal/ws_private/GetAnimalBlank/';

        this.http.get<any[]>(url, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          for(let s of response['speciess']) {
            var species = this.speciess.find(a => a.id == s['id'])
            if(!species){
              species = new Species()
              species.id = s['id'];
              this.speciess.push(species);
            }
            species.name = s['name'];
            species.hasVaccines = s['hasVaccines'];
          }

          if('diseasesList' in response) {
            for(let s of response['diseasesList']) {
              let d = this.diseaseService.diseases.find(a => a.id == s['id'])
              if(!d){
                d = new Disease()
                d.id = s['id'];
                this.diseaseService.diseases.push(d);
              }
              d.name = s['name'];
              d.species = this.speciess.find(a => a.id == s['speciesId']) ?? null
            }
          }
          if('surgicalOperationList' in response) {
            for(let s of response['surgicalOperationList']) {
              let d = this.surgicalOperationService.surgicalOperations.find(a => a.id == s['id'])
              if(!d){
                d = new SurgicalOperation()
                d.id = s['id'];
                this.surgicalOperationService.surgicalOperations.push(d);
              }
              d.name = s['name'];
              d.species = this.speciess.find(a => a.id == s['speciesId']) ?? null
            }
          }

          if(this.userService.user?.organization) {
            if('refugeCategoriesList' in response) {
              for(let s of response['refugeCategoriesList']) {
                let d = this.userService.user.organization.refugeCategories.find(a => a.id == s['id'])
                if(!d){
                  d = new AnimalRefugeCategory()
                  d.id = s['id'];
                  this.userService.user.organization.refugeCategories.push(d);
                }
                d.name = s['name'];
                d.description = s['description'];
                d.color = s['color'];
              }
            }
          }

          var animal = this.animals.find(a => a.id == animalId)
          if(!animal){
            animal = new Animal()
            this.animals.push(animal);
          }
          
          animal.id = response['id']
          animal.userId = response['userId']

          animal.pedigreeAnimalIs = response['pedigreeAnimalIs']
          animal.pedigreeUserId = response['pedigreeUserId']
          animal.pedigreeOwnerName = response['pedigreeOwnerName']

          animal.userFirstName = response['userFirstName']
          animal.userLastName = response['userLastName']
          animal.userIsRefuge = response['userIsRefuge']
          animal.isShareWithMe = response['isShareWithMe']
          animal.isShareIsWrite = response['isShareIsWrite']
          animal.hasEditRights = response['hasEditRights']

          animal.breederReserveIs = response['breederReserveIs']
          
          animal.nameOfficial = response['nameOfficial']
          animal.name = response['name']
          animal.affix = response['affix']
          animal.chip = response['chip']
          animal.race = response['race']
          animal.birthday = response['birthday']
          animal.isHidden = response['isHidden']
          animal.passportCode = response['passportCode']
          animal.isLOF = response['isLOF']
          animal.isConfirmed = response['isConfirmed']
          animal.avatar = response['avatar']
          animal.gender = response['gender']
          animal.category = response['category']
          animal.description = response['description']
          animal.geneticTests = response['geneticTests']
          animal.isDead = response['isDead']
          animal.dateDeath = response['dateDeath']

          animal.specificFood = response['specificFood']
          animal.descriptionFood = response['descriptionFood']

          animal.refugeHistory = response['refugeHistory']
          animal.refugeCharacter = response['refugeCharacter']
          animal.refugeAgreements = response['refugeAgreements']
          animal.refugeAdoptingProfile = response['refugeAdoptingProfile']
          animal.refugeAdoptionAvailable = response['refugeAdoptionAvailable']
          animal.refugeVisibleOnPublicPage = response['refugeVisibleOnPublicPage']
          animal.refugeBitingIs = response['refugeBitingIs']
          animal.refugeMuzzleIs = response['refugeMuzzleIs']

          animal.refugeCollarIs = response['refugeCollarIs']
          animal.refugeHarnessIs = response['refugeHarnessIs']
          animal.refugeDogOkLevel = response['refugeDogOkLevel']
          animal.refugeCatOkLevel = response['refugeCatOkLevel']
          animal.refugeWomanOkLevel = response['refugeWomanOkLevel']
          animal.refugeManOkLevel = response['refugeManOkLevel']
          animal.refugeKidsOkLevel = response['refugeKidsOkLevel']
          animal.refugeCarOkLevel = response['refugeCarOkLevel']
          animal.refugeBikeOkLevel = response['refugeBikeOkLevel']
          animal.refugeRunnerOkLevel = response['refugeRunnerOkLevel']
          animal.refugeHealth = response['refugeHealth']
          animal.refugeEnclosureOKIs = response['refugeEnclosureOKIs']
          animal.refugeWalkingInfo = response['refugeWalkingInfo']
          animal.organizationDateEntry = response['organizationDateEntry']
          
          if(response['refugeCategory']) {
            if(this.userService.user?.organization) {
              animal.refugeCategory = this.userService.user.organization.refugeCategories.find(a => a.id == response['refugeCategory']['id']) ?? null
              if(!animal.refugeCategory) {
                animal.refugeCategory = new AnimalRefugeCategory()
                this.userService.user.organization.refugeCategories.push(animal.refugeCategory)
              }
            } else {
              animal.refugeCategory = new AnimalRefugeCategory()
            }
            animal.refugeCategory.id = response['refugeCategory']['id']
            animal.refugeCategory.name = response['refugeCategory']['name']
            animal.refugeCategory.description = response['refugeCategory']['description']
            animal.refugeCategory.color = response['refugeCategory']['color']
          }

          if(response['flowControls'] && this.userService.user?.organization) {
            for(let m of response['flowControls']) {
              var flowControl = this.userService.user?.organization.flowControls.find(a => a.id == m['id'])
              if(!flowControl){
                flowControl = new FlowControl()
                this.userService.user?.organization.flowControls.push(flowControl);
              }
  
              flowControl.id = m['id']
              flowControl.userId = m['userId']
              flowControl.firstName = m['firstName']
              flowControl.lastName = m['lastName']
              flowControl.date = m['date']
              flowControl.animalId = m['animalId']
              flowControl.animalName = m['animalName']
              flowControl.action = m['action']
            }
          }

          if(response['temporaryAdoptions'] && this.userService.user?.organization) {
            for(let m of response['temporaryAdoptions']) {
              var temporaryAdoption = animal.temporaryAdoptions.find(a => a.id == m['id'])
              if(!temporaryAdoption){
                temporaryAdoption = new AnimalTemporaryAdoption()
                animal.temporaryAdoptions.push(temporaryAdoption);
              }
  
              temporaryAdoption.id = m['id']
              temporaryAdoption.userId = m['userId']
              temporaryAdoption.firstName = m['firstName']
              temporaryAdoption.lastName = m['lastName']
              temporaryAdoption.dateStart = m['dateStart']
              temporaryAdoption.dateEnd = m['dateEnd']
              temporaryAdoption.email = m['email']
              temporaryAdoption.addr = m['addr']
              temporaryAdoption.phoneNumber = m['phoneNumber']
            }
          }

          animal.mapPlanItem = new AnimalMapPlanItem() 
          animal.mapPlanItem.id = response['mapPlanItem']['id']
          animal.mapPlanItem.name = response['mapPlanItem']['name']
          animal.mapPlanItem.polyPoints = response['mapPlanItem']['polyPoints']
          
          animal.fosterFamily = null
          if(response['fosterFamily']) {
            animal.fosterFamily = new OrganizationFosterFamily()
            animal.fosterFamily.id = response['fosterFamily']['id']
            animal.fosterFamily.firstName = response['fosterFamily']['firstName']
            animal.fosterFamily.lastName = response['fosterFamily']['lastName']
            animal.fosterFamily.phoneNumber = response['fosterFamily']['phoneNumber']
            animal.fosterFamily.address = response['fosterFamily']['address']
            animal.fosterFamily.email = response['fosterFamily']['email']
          }
          
          animal.species = this.speciess.find(a => a.id == response['species']['id']);

          for(let d of response['documents']) {
            var document = new AnimalDocument()
            document.id = d['id']
            document.file = d['file']
            document.name = d['name']
            document.fileName = d['file']
            animal.documents.push(document);
          }
          
          animal.valences = []
          if('vaccines' in response ) {
            for(let v of response['vaccines']) {
              var valence: VaccineValence = new VaccineValence()
              valence.id = v['valence']['id']
              valence.name = v['valence']['name']
              valence.fullName = v['valence']['fullName']
              valence.description = v['valence']['description']
              valence.isDisplayOnHome = v['valence']['isDisplayOnHome']
              valence.species = animal.species

              var animalValence: AnimalVaccineValence = new AnimalVaccineValence()
              animalValence.valence = valence
              animalValence.isWarning = v['isWarning']
              animalValence.isIgnoreLatestError = v['isIgnoreLatestError']

              animal.valences.push(animalValence)

              for(let vaccine of v['vaccines'].concat(v['vaccinesIgnored'])) {
                var animalVaccine: AnimalVaccine = new AnimalVaccine()

                if(!vaccine['isError']) {
                  animalVaccine.isError = false

                } else {
                  animalVaccine.isError = true
                  animalVaccine.errorCode = vaccine['errorCode']
                }

                animalVaccine.id = vaccine['id']
                animalVaccine.date = vaccine['date']
                animalVaccine.dateValidStart = vaccine['dateValidStart']
                animalVaccine.dateEnd = vaccine['dateEnd']
                animalVaccine.isIgnorePreviousError = vaccine['isIgnorePreviousError']
                animalVaccine.file = vaccine['file']
                animalVaccine.lateDays = vaccine['lateDays'] || 0

                if('label' in vaccine) {
                  animalVaccine.label = new VaccineLabel()
                  animalVaccine.label.id = vaccine['label']['id']
                  animalVaccine.label.valences.push(valence)
                  animalVaccine.label.name = vaccine['label']['name']
                }
                if('configuration' in vaccine) {
                  animalVaccine.configuration = new VaccineConfiguration()
                  animalVaccine.configuration.id = vaccine['configuration']['id']
                  animalVaccine.configuration.duration = vaccine['configuration']['duration']
                  animalVaccine.configuration.durationMinBeforeNext = vaccine['configuration']['durationMinBeforeNext']
                  animalVaccine.configuration.durationValidAfter = vaccine['configuration']['durationValidAfter']
                  animalVaccine.configuration.injectionNb = vaccine['configuration']['injectionNb']
                  animalVaccine.configuration.isBooster = vaccine['configuration']['isBooster']
                  animalVaccine.configuration.ageLessDays = vaccine['configuration']['ageLessDays']
                  animalVaccine.configuration.ageSuqEqDays = vaccine['configuration']['ageSuqEqDays']
                  animalVaccine.configuration.specBB812 = vaccine['configuration']['specBB812']
                }


                if(v['vaccines'].includes(vaccine))
                  animalValence.vaccines.push(animalVaccine)
                else
                  animalValence.vaccinesIgnored.push(animalVaccine)
              }

              animalValence.vaccines.sort((a: AnimalVaccine, b: AnimalVaccine) => (
                (this.dateUtils.fromString(b.dateValidStart)?.getTime() ?? 0) == (this.dateUtils.fromString(a.dateValidStart)?.getTime() ?? 0) ? (b.isError ? -1: 1):
                (this.dateUtils.fromString(b.dateValidStart)?.getTime() ?? 0) - (this.dateUtils.fromString(a.dateValidStart)?.getTime() ?? 0)
              ))
            }
          }

          animal.diseases = []
          for(let d of response['diseases']) {
            var disease = new AnimalDisease()
            disease.id = d['id']
            disease.date = d['date']
            disease.dateEnd = d['dateEnd']
            disease.isEnd = d['isEnd']

            disease.disease = this.diseaseService.diseases.find(a => a.id == d['disease']['id']) ?? null
            if(!disease.disease){
              disease.disease = new Disease()
              disease.disease.id = d['disease']['id'];
              this.diseaseService.diseases.push(disease.disease);
            }
            disease.disease.name = d['disease']['name'];
            disease.disease.species = this.speciess.find(a => a.id == d['disease']['speciesId']) ?? null;

            animal.diseases.push(disease)
          }

          animal.surgicalOperations = []
          for(let d of response['surgicalOperations']) {
            var surgicalOperation = new AnimalSurgicalOperation()
            surgicalOperation.id = d['id']
            surgicalOperation.date = d['date']

            surgicalOperation.surgicalOperation = this.surgicalOperationService.surgicalOperations.find(a => a.id == d['surgicalOperation']['id']) ?? null
            if(!surgicalOperation.surgicalOperation){
              surgicalOperation.surgicalOperation = new SurgicalOperation()
              surgicalOperation.surgicalOperation.id = d['surgicalOperation']['id'];
              this.surgicalOperationService.surgicalOperations.push(surgicalOperation.surgicalOperation);
            }
            surgicalOperation.surgicalOperation.name = d['surgicalOperation']['name'];
            surgicalOperation.surgicalOperation.species = this.speciess.find(a => a.id == d['surgicalOperation']['speciesId']) ?? null;

            animal.surgicalOperations.push(surgicalOperation)
          }

          for(let d of response['treatmentsNotClosed']) {
            var treatment = agendaService.events.find(a => a.id == d['id'])
            if(!treatment){
              treatment = new AgendaEvent()
              agendaService.events.push(treatment);
            }

            treatment.type = agendaService.agendaEventTypes.find(a => a.id == d['type']['id']) ?? null;
            if(!treatment.type){
              treatment.type = new AgendaEventType()
              treatment.type.id = d['type']['id']
              treatment.type.name = d['type']['name']
              treatment.type.isTreatment = d['type']['isTreatment']
              agendaService.agendaEventTypes.push(treatment.type);
            }

            treatment.statut = agendaService.agendaEventStatuts.find(a => a.id == d['statut']['id']) ?? null;
            if(!treatment.statut) {
              treatment.statut = new AgendaEventStatut()
              treatment.statut.id = d['statut']['id'];
              treatment.statut.name = d['statut']['name'];
              agendaService.agendaEventStatuts.push(treatment.statut);
            }

            treatment.id = d['id']
            treatment.dateTimeStart = d['dateTimeStart']
            treatment.dateTimeEnd = d['dateTimeEnd'] || null
            treatment.comment = d['comment']
            treatment.treatmentIsSuccess = d['treatmentIsSuccess']
            if(!treatment.animals.map(a => a.id).includes(animal.id ?? 0))
              treatment.animals.push(animal)
          }

          animal.weights = []
          if('weights' in response) {
            for(let d of response['weights']) {
              var weight = new AnimalWeight()
              weight.id = d['id']
              weight.date = d['date']
              weight.weight = d['weight']
              animal.weights.push(weight)
            }
          }
          animal.weights = animal.weights.sort((a: AnimalWeight, b: AnimalWeight) => (b.date + '_' + b.weight).localeCompare(a.date + '_' + a.weight))

          animal.tags = []
          if('tags' in response) {
            for(let t of response['tags']) {
              var tag = new AnimalTag()
              tag.id = t['id']
              tag.date = t['date']
              tag.tag = t['tag']
              animal.tags.push(tag)
            }
          }

          if(response['avatarBase64'] != null) {
            animal.avatarBase64 = response['avatarBase64'];
            resolve(animal);
          } else if(animal.avatar != null) {
            animal.avatarBase64 = null;
            this.http.get(response['avatar'],  { responseType: 'blob' as 'blob' }).subscribe(data => {
              blobToBase64(data).then((res: any) =>  {
                if(animal)
                  animal.avatarBase64 = res;
                resolve(animal);
              });
            },() => {
              resolve(animal);
            });
          } else {
            resolve(animal);
          }


          if(response['father']) {
            if(response['father']['species']) {
              animal.father = this.convertHttpResponsesAnimals(null, {'animals': [response['father']]})[0];
            } else if (response['father']['id'] != animal.father?.id) {
              animal.father = this.animals.find(a => a.id == response['father']['id']) ?? new Animal();
              animal.father.id = response['father']['id'];
            }
          }

          if(response['mother']) {
            if(response['mother']['species']) {
              animal.mother = this.convertHttpResponsesAnimals(null, {'animals': [response['mother']]})[0];
            } else if (response['mother']['id'] != animal.mother?.id) {
              animal.mother = this.animals.find(a => a.id == response['mother']['id']) ?? new Animal();
              animal.mother.id = response['mother']['id'];
            }
          }
        },
        () => {
          resolve(this.animals.find(a => a.id == animalId));
        })
      } else {
        resolve(cache);
      }
    });
  }

  convertHttpResponsesAnimals(agendaService: AgendaService | null, response: { [key: string]: any }) {
    var animals: Animal[] =[]

    if('speciess' in response) {
      for(let s of response['speciess']) {
        let species = this.speciess.find(a => a.id == s['id'])
        if(!species){
          species = new Species()
          species.id = s['id'];
          this.speciess.push(species);
        }
        species.name = s['name'];
        species.hasVaccines = s['hasVaccines'];
      }
    }

    if('diseasesList' in response) {
      for(let s of response['diseasesList']) {
        let d = this.diseaseService.diseases.find(a => a.id == s['id'])
        if(!d){
          d = new Disease()
          d.id = s['id'];
          d.species = this.speciess.find(a => a.id == s['speciesId']) ?? null
          this.diseaseService.diseases.push(d);
        }
        d.name = s['name'];
      }
    }
    if('surgicalOperationList' in response) {
      for(let s of response['surgicalOperationList']) {
        let d = this.surgicalOperationService.surgicalOperations.find(a => a.id == s['id'])
        if(!d){
          d = new SurgicalOperation()
          d.id = s['id'];
          d.species = this.speciess.find(a => a.id == s['speciesId']) ?? null
          this.surgicalOperationService.surgicalOperations.push(d);
        }
        d.name = s['name'];
      }
    }

    if(this.userService.user?.organization) {
      if('refugeCategoriesList' in response) {
        for(let s of response['refugeCategoriesList']) {
          let d = this.userService.user.organization.refugeCategories.find(a => a.id == s['id'])
          if(!d){
            d = new AnimalRefugeCategory()
            d.id = s['id'];
            this.userService.user.organization.refugeCategories.push(d);
          }
          d.name = s['name'];
          d.description = s['description'];
          d.color = s['color'];
        }
      }
    }

    for(let a of response['animals']) {
      var animal = this.animals.find(b => b.id == a['id'])
      if(!animal){
        animal = new Animal()
        this.animals.push(animal);
      }
      animal.id = a['id']
      animal.userId = a['userId']
      animal.pedigreeAnimalIs = a['pedigreeAnimalIs']
      animal.pedigreeUserId = a['pedigreeUserId']
      animal.pedigreeOwnerName = a['pedigreeOwnerName']
      animal.userFirstName = a['userFirstName']
      animal.userLastName = a['userLastName']
      animal.userIsRefuge = a['userIsRefuge']
      animal.isShareWithMe = a['isShareWithMe']
      animal.isShareIsWrite = a['isShareIsWrite']
      animal.hasEditRights = a['hasEditRights']
      animal.nameOfficial = a['nameOfficial']
      animal.name = a['name']
      animal.affix = a['affix']
      animal.chip = a['chip']
      animal.race = a['race']
      animal.birthday = a['birthday']
      animal.isHidden = a['isHidden']
      animal.passportCode = a['passportCode']
      animal.isLOF = a['isLOF']
      animal.isConfirmed = a['isConfirmed']
      animal.avatar = a['avatar']
      animal.gender = a['gender']
      animal.category = a['category']
      animal.description = a['description']
      animal.geneticTests = a['geneticTests']
      animal.displayOnPublicPage = a['displayOnPublicPage']
      animal.isDead = a['isDead']
      animal.dateDeath = a['dateDeath']
      animal.specificFood = a['specificFood']
      animal.descriptionFood = a['descriptionFood']

      animal.breederReserveIs = response['breederReserveIs']

      animal.refugeHistory = a['refugeHistory']
      animal.refugeCharacter = a['refugeCharacter']
      animal.refugeAgreements = a['refugeAgreements']
      animal.refugeAdoptingProfile = a['refugeAdoptingProfile']
      animal.refugeAdoptionAvailable = a['refugeAdoptionAvailable']
      animal.refugeBitingIs = a['refugeBitingIs']
      animal.refugeMuzzleIs = a['refugeMuzzleIs']

      animal.refugeCollarIs = a['refugeCollarIs']
      animal.refugeHarnessIs = a['refugeHarnessIs']
      animal.refugeDogOkLevel = a['refugeDogOkLevel']
      animal.refugeCatOkLevel = a['refugeCatOkLevel']
      animal.refugeWomanOkLevel = a['refugeWomanOkLevel']
      animal.refugeManOkLevel = a['refugeManOkLevel']
      animal.refugeKidsOkLevel = a['refugeKidsOkLevel']
      animal.refugeCarOkLevel = a['refugeCarOkLevel']
      animal.refugeBikeOkLevel = a['refugeBikeOkLevel']
      animal.refugeRunnerOkLevel = a['refugeRunnerOkLevel']
      animal.refugeHealth = a['refugeHealth']
      animal.refugeEnclosureOKIs = a['refugeEnclosureOKIs']
      animal.refugeWalkingInfo = a['refugeWalkingInfo']
      animal.organizationDateEntry = a['organizationDateEntry']

      if(a['refugeCategory']) {
        if(this.userService.user?.organization) {
          animal.refugeCategory = this.userService.user.organization.refugeCategories.find(b => b.id == a['refugeCategory']['id']) ?? null
          if(!animal.refugeCategory) {
            animal.refugeCategory = new AnimalRefugeCategory()
            this.userService.user.organization.refugeCategories.push(animal.refugeCategory)
          }
        } else {
          animal.refugeCategory = new AnimalRefugeCategory()
        }
        animal.refugeCategory.id = a['refugeCategory']['id']
        animal.refugeCategory.name = a['refugeCategory']['name']
        animal.refugeCategory.description = a['refugeCategory']['description']
        animal.refugeCategory.color = a['refugeCategory']['color']
      }

      if(a['flowControls'] && this.userService.user?.organization) {
        for(let m of a['flowControls']) {
          var flowControl = this.userService.user?.organization.flowControls.find(a => a.id == m['id'])
          if(!flowControl){
            flowControl = new FlowControl()
            this.userService.user?.organization.flowControls.push(flowControl);
          }

          flowControl.id = m['id']
          flowControl.userId = m['userId']
          flowControl.firstName = m['firstName']
          flowControl.lastName = m['lastName']
          flowControl.date = m['date']
          flowControl.animalId = m['animalId']
          flowControl.animalName = m['animalName']
          flowControl.action = m['action']
        }
      }


      if(a['temporaryAdoptions'] && this.userService.user?.organization) {
        for(let m of a['temporaryAdoptions']) {
          var temporaryAdoption = animal.temporaryAdoptions.find(a => a.id == m['id'])
          if(!temporaryAdoption){
            temporaryAdoption = new AnimalTemporaryAdoption()
            animal.temporaryAdoptions.push(temporaryAdoption);
          }

          temporaryAdoption.id = m['id']
          temporaryAdoption.userId = m['userId']
          temporaryAdoption.firstName = m['firstName']
          temporaryAdoption.lastName = m['lastName']
          temporaryAdoption.dateStart = m['dateStart']
          temporaryAdoption.dateEnd = m['dateEnd']
          temporaryAdoption.email = m['email']
          temporaryAdoption.addr = m['addr']
          temporaryAdoption.phoneNumber = m['phoneNumber']
        }
      }

      if('mapPlanItem' in a) {
        animal.mapPlanItem = new AnimalMapPlanItem() 
        animal.mapPlanItem.id = a['mapPlanItem']['id']
        animal.mapPlanItem.name = a['mapPlanItem']['name']
        animal.mapPlanItem.polyPoints = a['mapPlanItem']['polyPoints']
      }

      animal.fosterFamily = null
      if(a['fosterFamily']) {
        animal.fosterFamily = new OrganizationFosterFamily()
        animal.fosterFamily.id = a['fosterFamily']['id']
        animal.fosterFamily.firstName = a['fosterFamily']['firstName']
        animal.fosterFamily.lastName = a['fosterFamily']['lastName']
        animal.fosterFamily.phoneNumber = a['fosterFamily']['phoneNumber']
        animal.fosterFamily.address = a['fosterFamily']['address']
        animal.fosterFamily.email = a['fosterFamily']['email']
      }

      animal.species = this.speciess.find(b => b.id == a['species']['id']);
      if(!animal.species) {
        var species = new Species()
        species.id = a['species']['id']
        species.name = a['species']['name']
        species.hasVaccines = a['species']['hasVaccines'];
        this.speciess.push(species);
        animal.species = species;
      }

      if('vaccines' in a ) {
        animal.valences = []
        for(let v of a['vaccines']) {
          var valence: VaccineValence = new VaccineValence()
          valence.id = v['valence']['id']
          valence.name = v['valence']['name']
          valence.fullName = v['valence']['fullName']
          valence.description = v['valence']['description']
          valence.isDisplayOnHome = v['valence']['isDisplayOnHome']
          valence.species = animal.species

          var animalValence: AnimalVaccineValence = new AnimalVaccineValence()
          animalValence.valence = valence
          animalValence.isWarning = v['isWarning']
          animalValence.isIgnoreLatestError = v['isIgnoreLatestError']

          animal.valences.push(animalValence)

          for(let vaccine of v['vaccines'].concat(v['vaccinesIgnored'])) {
            var animalVaccine: AnimalVaccine = new AnimalVaccine()

            if(!vaccine['isError']) {
              animalVaccine.isError = false

            } else {
              animalVaccine.isError = true
              animalVaccine.errorCode = vaccine['errorCode']
            }

            animalVaccine.id = vaccine['id']
            animalVaccine.date = vaccine['date']
            animalVaccine.dateValidStart = vaccine['dateValidStart']
            animalVaccine.dateEnd = vaccine['dateEnd']
            animalVaccine.isIgnorePreviousError = vaccine['isIgnorePreviousError']
            animalVaccine.file = vaccine['file']
            animalVaccine.lateDays = vaccine['lateDays'] || 0
            
            if('label' in vaccine) {
              animalVaccine.label = new VaccineLabel()
              animalVaccine.label.id = vaccine['label']['id']
              animalVaccine.label.valences.push(valence)
              animalVaccine.label.name = vaccine['label']['name']
            }
            if('configuration' in vaccine) {
              animalVaccine.configuration = new VaccineConfiguration()
              animalVaccine.configuration.id = vaccine['configuration']['id']
              animalVaccine.configuration.duration = vaccine['configuration']['duration']
              animalVaccine.configuration.durationMinBeforeNext = vaccine['configuration']['durationMinBeforeNext']
              animalVaccine.configuration.durationValidAfter = vaccine['configuration']['durationValidAfter']
              animalVaccine.configuration.injectionNb = vaccine['configuration']['injectionNb']
              animalVaccine.configuration.isBooster = vaccine['configuration']['isBooster']
              animalVaccine.configuration.ageLessDays = vaccine['configuration']['ageLessDays']
              animalVaccine.configuration.ageSuqEqDays = vaccine['configuration']['ageSuqEqDays']
              animalVaccine.configuration.specBB812 = vaccine['configuration']['specBB812']
            }

            if(v['vaccines'].includes(vaccine))
              animalValence.vaccines.push(animalVaccine)
            else
              animalValence.vaccinesIgnored.push(animalVaccine)
          }

          animalValence.vaccines.sort((a: AnimalVaccine, b: AnimalVaccine) => (
            (this.dateUtils.fromString(b.dateValidStart)?.getTime() ?? 0) == (this.dateUtils.fromString(a.dateValidStart)?.getTime() ?? 0) ? (b.isError ? -1: 1):
            (this.dateUtils.fromString(b.dateValidStart)?.getTime() ?? 0) - (this.dateUtils.fromString(a.dateValidStart)?.getTime() ?? 0)
          ))
        }
      }

      if(a['diseases']) {
        animal.diseases = []
        for(let d of a['diseases']) {
          var disease = new AnimalDisease()
          disease.id = d['id']
          disease.date = d['date']
          disease.dateEnd = d['dateEnd']
          disease.isEnd = d['isEnd']

          disease.disease = this.diseaseService.diseases.find(a => a.id == d['disease']['id']) ?? null
          if(!disease.disease){
            disease.disease = new Disease()
            disease.disease.id = d['disease']['id'];
            this.diseaseService.diseases.push(disease.disease);
          }
          disease.disease.name = d['disease']['name'];
          disease.disease.species = this.speciess.find(a => a.id == d['disease']['speciesId']) ?? null;

          animal.diseases.push(disease)
        }
      }

      if(a['surgicalOperations']) {
        animal.surgicalOperations = []
        for(let d of a['surgicalOperations']) {
          var surgicalOperation = new AnimalSurgicalOperation()
          surgicalOperation.id = d['id']
          surgicalOperation.date = d['date']

          surgicalOperation.surgicalOperation = this.surgicalOperationService.surgicalOperations.find(a => a.id == d['surgicalOperation']['id']) ?? null
          if(!surgicalOperation.surgicalOperation){
            surgicalOperation.surgicalOperation = new SurgicalOperation()
            surgicalOperation.surgicalOperation.id = d['surgicalOperation']['id'];
            this.surgicalOperationService.surgicalOperations.push(surgicalOperation.surgicalOperation);
          }
          surgicalOperation.surgicalOperation.name = d['surgicalOperation']['name'];
          surgicalOperation.surgicalOperation.species = this.speciess.find(a => a.id == d['surgicalOperation']['speciesId']) ?? null;

          animal.surgicalOperations.push(surgicalOperation)
        }
      }

      if(agendaService && a['treatmentsNotClosed']) {
        for(let d of a['treatmentsNotClosed']) {
          var treatment = agendaService.events.find(a => a.id == d['id'])
          if(!treatment){
            treatment = new AgendaEvent()
            agendaService.events.push(treatment);
          }

          treatment.type = agendaService.agendaEventTypes.find(a => a.id == d['type']['id']) ?? null;
          if(!treatment.type){
            treatment.type = new AgendaEventType()
            treatment.type.id = d['type']['id']
            treatment.type.name = d['type']['name']
            treatment.type.isTreatment = d['type']['isTreatment']
            agendaService.agendaEventTypes.push(treatment.type);
          }

          treatment.statut = agendaService.agendaEventStatuts.find(a => a.id == d['statut']['id']) ?? null;
          if(!treatment.statut) {
            treatment.statut = new AgendaEventStatut()
            treatment.statut.id = d['statut']['id'];
            treatment.statut.name = d['statut']['name'];
            agendaService.agendaEventStatuts.push(treatment.statut);
          }

          treatment.id = d['id']
          treatment.dateTimeStart = d['dateTimeStart']
          treatment.dateTimeEnd = d['dateTimeEnd'] || null
          treatment.comment = d['comment']
          treatment.treatmentIsSuccess = d['treatmentIsSuccess']
          if(!treatment.animals.map(a => a.id).includes(animal.id ?? 0))
            treatment.animals.push(animal)
        }
      }

      if(a['weights']) {
        animal.weights = []
        for(let d of a['weights']) {
          var weight = new AnimalWeight()
          weight.id = d['id']
          weight.date = d['date']
          weight.weight = d['weight']
          animal.weights.push(weight)
        }
      }
      animal.weights = animal.weights.sort((a: AnimalWeight, b: AnimalWeight) => (b.date + '_' + b.weight).localeCompare(a.date + '_' + a.weight))
      
      if(a['tags']) {
        animal.tags = []
        for(let t of a['tags']) {
          var tag = new AnimalTag()
          tag.id = t['id']
          tag.date = t['date']
          tag.tag = t['tag']
          animal.tags.push(tag)
        }
      }

      animals.push(animal);

      animal.avatarBase64 = null;
      if(a['avatarBase64'] != null) {
        animal.avatarBase64 = a['avatarBase64'];
      } else if(animal.avatar != null) {
        this.http.get(a['avatar'],  { responseType: 'blob' as 'blob' }).subscribe(data => {
          blobToBase64(data).then((res: any) =>  {
            if(animal)
              animal.avatarBase64 = res;
          });
        });
      }

      if(a['father']) {
        if(a['father']['species']) {
          animal.father = this.convertHttpResponsesAnimals(null, {'animals': [a['father']]})[0];
        } else {
          animal.father = new Animal();
          animal.father.id = a['father']['id'];
        }
      }

      if(a['mother']) {
        if(a['mother']['species']) {
          animal.mother = this.convertHttpResponsesAnimals(null, {'animals': [a['mother']]})[0];
        } else {
          animal.mother = new Animal();
          animal.mother.id = a['mother']['id'];
        }
      }
    }

    return animals;
  }

  dbGetAnimalsOfUserLatestUpdate: Date | null = null;
  dbGetAnimalsOfUser(agendaService: AgendaService, forceUpdate: boolean, userId: number): Promise<Animal[] | null> {
    forceUpdate = true; //la liste des animaux depuis une organisation ne focntionne pas avec le cache
    var cache: Animal[] = this.animals.filter(a => a.userId == userId || a.isShareWithMe)
    return new Promise((resolve) => {
      if ((forceUpdate || cache.length == 0 || !this.dbGetAnimalsOfUserLatestUpdate || Math.abs(this.dbGetAnimalsOfUserLatestUpdate.getTime() - (new Date()).getTime()) > 5 * 60 * 1000) && this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'animal/ws_private/' + userId + '/GetAnimalsOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          this.dbGetAnimalsOfUserLatestUpdate = new Date()
          var animals: Animal[] = this.convertHttpResponsesAnimals(agendaService, response)
          resolve(animals);
        },
        () => {
          resolve(this.animals.filter(a => a.userId == userId || a.isShareWithMe));
        })
      } else {
        resolve(cache);
      }
    });
  }

  dbGetAnimalPedigree(animalId: number): Promise<Animal | null> {
    return new Promise((resolve) => {
      let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
      });
      this.http.get<any>(environment.API_URL + 'animal/ws_public/' + animalId + '/GetAnimalPedigree/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
        response['animals'] = [response['animal']]
        var animals: Animal[] = this.convertHttpResponsesAnimals(null, response)
        resolve(animals[0]);
      },
      (error) => {
        resolve(null);
      });
    });
  }

  dbGetAnimalPublic(animalId: number): Promise<Animal | null> {
    return new Promise((resolve) => {
      let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
      });
      this.http.get<any>(environment.API_URL + 'animal/ws_public/' + animalId + '/GetAnimal/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
        response['animals'] = [response['animal']]
        var animals: Animal[] = this.convertHttpResponsesAnimals(null, response)
        resolve(animals[0]);
      },
      (error) => {
        resolve(null);
      });
    });
  }

  dbGetPublicAnimalsOfUser(userIdentifier: string): Promise<{ animals: Animal[], user: User }> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'animal/ws_public/' + userIdentifier + '/GetAnimalsOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var animals: Animal[] = this.convertHttpResponsesAnimals(null, response)
          let user = new User()
          user.id = response['user']['id']
          user.firstName = response['user']['firstName']
          user.lastName = response['user']['lastName']

          resolve({animals: animals, user: user});
        },
        () => {
          resolve({animals: [], user: new User()});
        })
      } else {
        resolve({animals: [], user: new User()});
      }
    });
  }

  dbGetAnimalsOfOrganization(agendaService: AgendaService, organizationId: number): Promise<Animal[] | null> {
    return new Promise((resolve) => {
      let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
      });
      this.http.get<any[]>(environment.API_URL + 'animal/ws_private/' + organizationId + '/GetAnimalsOfOrganization/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
        var animals: Animal[] = this.convertHttpResponsesAnimals(agendaService, response)
        resolve(animals);
      },
      () => {
        resolve([]);
      })
    });
  }

  dbGetAnimalsOfOrganizationPublic(agendaService: AgendaService, organizationId: number): Promise<Animal[] | null> {
    return new Promise((resolve) => {
      let headers = new HttpHeaders({
          'Authorization': this.userService.getHttpHeaders()
      });
      this.http.get<any[]>(environment.API_URL + 'animal/ws_public/' + organizationId + '/GetAnimalsOfOrganization/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
        var animals: Animal[] = this.convertHttpResponsesAnimals(agendaService, response)
        resolve(animals);
      },
      () => {
        resolve([]);
      })
    });
  }

  dbSaveAnimalVaccineIgnoreLatestError(animal: Animal, animalVaccineValence: AnimalVaccineValence,) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["animalId"] = animal.id;
        formData["vaccineValenceId"] = animalVaccineValence.valence?.id;
        formData["isIgnoreLatestError"] = animalVaccineValence.isIgnoreLatestError;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimalVaccineIgnoreLatestError/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animal);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        resolve(animal)
      }
    });
  }

  dbSaveAnimalVaccine(animal: Animal, animalVaccine: AnimalVaccine) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalVaccine.id;
        formData["animalId"] = animal.id;
        formData["labelId"] = animalVaccine.label?.id;
        formData["date"] = animalVaccine.date;
        formData["isIgnorePreviousError"] = animalVaccine.isIgnorePreviousError;

        if(animalVaccine.fileNewName != null) {
          formData["file"] = {'base64': animalVaccine.fileBase64, 'name': animalVaccine.fileNewName}
        }
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimalVaccine/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            animalVaccine.id = response['id']
            resolve(animalVaccine);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalVaccine, 'action': 'save'}).subscribe(() => {});
        resolve(animalVaccine)
      }
    });
  }

  /**
   * 
   * @param animal 
   * @param animalVaccines 
   * @param allVaccinesHasSamePhoto Pour économiser le webservice, on envoie l'image qu'une seule fois si tous les vaccins ont la même photo
   * @returns 
   */
  dbSaveAnimalVaccines(animal: Animal, animalVaccines: AnimalVaccine[], allVaccinesHasSamePhoto: boolean = false) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {
          'animalId': animal.id, 
          'allVaccinesHasSamePhoto': allVaccinesHasSamePhoto && animalVaccines[0].fileNewName, 
          'vaccines': []};
        
        if(allVaccinesHasSamePhoto && animalVaccines[0].fileNewName) {
          formData['file'] = {'name': animalVaccines[0].fileNewName, 'base64': animalVaccines[0].fileBase64}
        }

        for(var animalVaccine of animalVaccines) {
          formData['vaccines'].push({
            'id': animalVaccine.id,
            'labelId': animalVaccine.label?.id,
            'date': animalVaccine.date,
            'isIgnorePreviousError': animalVaccine.isIgnorePreviousError,
            'file': animalVaccine.fileNewName ? {'name': animalVaccine.fileNewName, 'base64': animalVaccine.fileBase64}: null,
          })
        }
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/AddAnimalVaccines/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            var index: number = 0;
            for(var v of response['vaccines']) {
              animalVaccines[index].id = v['id']
              index += 1
            }
            resolve(animalVaccines);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        for(var animalVaccine of animalVaccines) {
          this.indexedDbService.addItem({'obj': animalVaccine, 'action': 'save'}).subscribe(() => {});
        }
        resolve(animalVaccines)
      }
    });
  }

  dbDeleteAnimalVaccine(animal: Animal, animalVaccine: AnimalVaccine) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalVaccine.id;
        formData["animalId"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalVaccine/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalVaccine);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalVaccine, 'action': 'delete'}).subscribe(() => {});
        resolve(animalVaccine)
      }
    });
  }

  dbDeleteAnimalWeight(animal: Animal, animalWeight: AnimalWeight) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalWeight.id;
        formData["animalId"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalWeight/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalWeight);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalWeight, 'action': 'delete'}).subscribe(() => {});
        resolve(animalWeight)
      }
    });
  }

  dbDeleteAnimalDisease(animal: Animal, animalDisease: AnimalDisease) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalDisease.id;
        formData["animalId"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalDisease/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalDisease);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalDisease, 'action': 'delete'}).subscribe(() => {});
        resolve(animalDisease)
      }
    });
  }

  dbDeleteAnimalSurgicalOperation(animal: Animal, animalSurgicalOperation: AnimalSurgicalOperation) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalSurgicalOperation.id;
        formData["animalId"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalSurgicalOperation/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalSurgicalOperation);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalSurgicalOperation, 'action': 'delete'}).subscribe(() => {});
        resolve(animalSurgicalOperation)
      }
    });
  }
  
  

  dbSaveAnimalTag(animal: Animal, animalTag: AnimalTag) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalTag.id;
        formData["animalId"] = animal.id;
        formData["tag"] = animalTag.tag;
        formData["date"] = animalTag.date;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimalTag/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            animalTag.id = response['id']
            resolve(animalTag);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalTag, 'action': 'save'}).subscribe(() => {});
        resolve(animalTag)
      }
    });
  }

  dbDeleteAnimalTag(animal: Animal, animalTag: AnimalTag) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalTag.id;
        formData["animalId"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalTag/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalTag);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalTag, 'action': 'delete'}).subscribe(() => {});
        resolve(animalTag)
      }
    });
  }


  dbGetAnimalSharesOfUser(forceUpdate: boolean, userId: number): Promise<{'sharesMy': AnimalShare[], 'sharesWithMe':AnimalShare[]} | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'animal/ws_private/' + userId + '/GetAnimalSharesOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var sharesMy: AnimalShare[] =[]
          for(let a of response['sharesMy']) {
            let animalShare = this.animalShares.find(b => b.id == a['id'])
            if(!animalShare){
              animalShare = new AnimalShare()
              this.animalShares.push(animalShare);
            }
            animalShare.id = a['id']
            animalShare.date = a['date']
            animalShare.isShareAllAnimals = a['isShareAllAnimals']
            animalShare.isSharePharmacy = a['isSharePharmacy']
            animalShare.userShareNotifAgendaIs = a['userShareNotifAgendaIs']
            animalShare.userShareNotifAnimalIs = a['userShareNotifAnimalIs']
            animalShare.isWrite = a['isWrite']

            animalShare.user = new AnimalShareUser()
            animalShare.user.id = a['user']['id']
            animalShare.user.firstName = a['user']['firstName']
            animalShare.user.lastName = a['user']['lastName']
            animalShare.user.email = a['user']['email']

            animalShare.userShare = new AnimalShareUser()
            animalShare.userShare.id = a['userShare']['id']
            animalShare.userShare.firstName = a['userShare']['firstName']
            animalShare.userShare.lastName = a['userShare']['lastName']
            animalShare.userShare.email = a['userShare']['email']

            animalShare.animals = []
            for(let an of a['animals']) {
              var animal = new AnimalShareAnimal()
              animal.id = an['id']
              animal.name = an['name']
              animalShare.animals.push(animal)
            }
            sharesMy.push(animalShare)
          }

          var sharesWithMe: AnimalShare[] =[]
          for(let a of response['sharesWithMe']) {
            let animalShare = this.animalShares.find(b => b.id == a['id'])
            if(!animalShare){
              animalShare = new AnimalShare()
              this.animalShares.push(animalShare);
            }
            animalShare.id = a['id']
            animalShare.date = a['date']
            animalShare.isShareAllAnimals = a['isShareAllAnimals']
            animalShare.isSharePharmacy = a['isSharePharmacy']
            animalShare.userShareNotifAgendaIs = a['userShareNotifAgendaIs']
            animalShare.userShareNotifAnimalIs = a['userShareNotifAnimalIs']
            animalShare.isWrite = a['isWrite']

            animalShare.user = new AnimalShareUser()
            animalShare.user.id = a['user']['id']
            animalShare.user.firstName = a['user']['firstName']
            animalShare.user.lastName = a['user']['lastName']
            animalShare.user.email = a['user']['email']

            animalShare.userShare = new AnimalShareUser()
            animalShare.userShare.id = a['userShare']['id']
            animalShare.userShare.firstName = a['userShare']['firstName']
            animalShare.userShare.lastName = a['userShare']['lastName']
            animalShare.userShare.email = a['userShare']['email']

            animalShare.animals = []
            for(let an of a['animals']) {
              var animal = new AnimalShareAnimal()
              animal.id = an['id']
              animal.name = an['name']
              animalShare.animals.push(animal)
            }
            sharesWithMe.push(animalShare)
          }
          resolve({'sharesMy': sharesMy, 'sharesWithMe': sharesWithMe});
        },
        () => {
          resolve({'sharesMy': this.animalShares.filter(a => a.user?.id == userId), 'sharesWithMe': this.animalShares.filter(a => a.userShare?.id == userId)});
        })
      } else {
        resolve({'sharesMy': this.animalShares.filter(a => a.user?.id == userId), 'sharesWithMe': this.animalShares.filter(a => a.userShare?.id == userId)});
      }
    });
  }

  dbGetAnimalShare(forceUpdate: boolean, animalShareId: number | null): Promise<AnimalShare | null | undefined> {
    var cache: AnimalShare | undefined = this.animalShares.find(a => a.id == animalShareId)
    return new Promise((resolve) => {
      if ((!animalShareId || forceUpdate || !cache) && this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });

        var url = environment.API_URL + 'animal/ws_private/' + animalShareId + '/GetAnimalShare/';
        if(animalShareId == null)
          url = environment.API_URL + 'animal/ws_private/GetAnimalShareBlank/';

        this.http.get<any[]>(url, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          var animalShare = this.animalShares.find(a => a.id == animalShareId)
          if(!animalShare){
            animalShare = new AnimalShare()
            this.animalShares.push(animalShare);
          }

          animalShare.id = response['id']
          animalShare.date = response['date']
          animalShare.isShareAllAnimals = response['isShareAllAnimals']
          animalShare.isSharePharmacy = response['isSharePharmacy']
          animalShare.userShareNotifAgendaIs = response['userShareNotifAgendaIs']
          animalShare.userShareNotifAnimalIs = response['userShareNotifAnimalIs']
          animalShare.isWrite = response['isWrite']

          animalShare.user = new AnimalShareUser()
          animalShare.user.id = response['user']['id']
          animalShare.user.firstName = response['user']['firstName']
          animalShare.user.lastName = response['user']['lastName']
          animalShare.user.email = response['user']['email']

          animalShare.userShare = new AnimalShareUser()
          animalShare.userShare.id = response['userShare']['id']
          animalShare.userShare.firstName = response['userShare']['firstName']
          animalShare.userShare.lastName = response['userShare']['lastName']
          animalShare.userShare.email = response['userShare']['email']

          animalShare.animals = []
          for(let an of response['animals']) {
            var animal = new AnimalShareAnimal()
            animal.id = an['id']
            animal.name = an['name']
            animalShare.animals.push(animal)
          }
          
          resolve(animalShare)
        },
        () => {
          resolve(this.animalShares.find(a => a.id == animalShareId));
        })
      } else {
        resolve(cache);
      }
    });
  }

  dbSaveAnimalShare(animalShare: AnimalShare): Promise<AnimalShare> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalShare.id;
        formData["userId"] = animalShare.user?.id;
        formData["userShareId"] = animalShare.userShare?.id;
        formData["isWrite"] = animalShare.isWrite;
        formData["isShareAllAnimals"] = animalShare.isShareAllAnimals;
        formData["isSharePharmacy"] = animalShare.isSharePharmacy;
        formData["userShareNotifAgendaIs"] = animalShare.userShareNotifAgendaIs;
        formData["userShareNotifAnimalIs"] = animalShare.userShareNotifAnimalIs;
        formData["animals"] = animalShare.animals.map(a => a.id);

        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/UpdateAnimalShare/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            animalShare.id = response['id']
            animalShare.date = response['date']
            animalShare.isShareAllAnimals = response['isShareAllAnimals']
            animalShare.isSharePharmacy = response['isSharePharmacy']
            animalShare.userShareNotifAgendaIs = response['userShareNotifAgendaIs']
            animalShare.userShareNotifAnimalIs = response['userShareNotifAnimalIs']
            animalShare.isWrite = response['isWrite']

            animalShare.user = new AnimalShareUser()
            animalShare.user.id = response['user']['id']
            animalShare.user.firstName = response['user']['firstName']
            animalShare.user.lastName = response['user']['lastName']
            animalShare.user.email = response['user']['email']
            
            animalShare.userShare = new AnimalShareUser()
            animalShare.userShare.id = response['userShare']['id']
            animalShare.userShare.firstName = response['userShare']['firstName']
            animalShare.userShare.lastName = response['userShare']['lastName']
            animalShare.userShare.email = response['userShare']['email']

            animalShare.animals = []
            for(let an of response['animals']) {
              var animal = new AnimalShareAnimal()
              animal.id = an['id']
              animal.name = an['name']
              animalShare.animals.push(animal)
            }

            resolve(animalShare);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalShare, 'action': 'save'}).subscribe(() => {});
        resolve(animalShare)
      }
    });
  }

  dbDeleteAnimalShare(animalShare: AnimalShare) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animalShare.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimalShare/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            resolve(animalShare);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animalShare, 'action': 'delete'}).subscribe(() => {});
        resolve(animalShare)
      }
    });
  }


  dbDeleteAnimal(animal: Animal) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.userService.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["id"] = animal.id;
        
        this.http.post<any[]>(environment.API_URL + 'animal/ws_private/DeleteAnimal/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            this.animals = this.animals.filter(a => a.id != animal.id);
            resolve(animal);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': animal, 'action': 'delete'}).subscribe(() => {});
        resolve(animal)
      }
    });
  }

}